import { AlertTriangle } from 'lucide-react';
import { Button } from '@/components/base/Button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/base/Modal';
import { formatDuration } from '@/lib/types/consultation';

export interface LeaveConsultationModalProps {
  open: boolean;
  patientName?: string;
  /** Whether the mic is actively recording right now (vs. paused). */
  isRecording: boolean;
  /** Whether a Save Draft submission is currently in flight. */
  isSavingDraft?: boolean;
  /** Recording duration, in seconds, snapshotted when the modal opens. */
  recordingSeconds?: number;
  symptomsCount?: number;
  diagnosesCount?: number;
  onExitWithoutSaving: () => void;
  onSaveDraftAndExit: () => void;
  onStay: () => void;
}

/**
 * Confirmation shown when the user tries to leave an in-progress
 * consultation (via the browser back button, tab close, or the in-app
 * Back action) before it has been saved or submitted.
 */
export function LeaveConsultationModal({
  open,
  patientName,
  isRecording,
  isSavingDraft = false,
  recordingSeconds = 0,
  symptomsCount = 0,
  diagnosesCount = 0,
  onExitWithoutSaving,
  onSaveDraftAndExit,
  onStay,
}: LeaveConsultationModalProps) {
  return (
    <Dialog open={open} onOpenChange={(next) => !next && onStay()}>
      <DialogContent
        size="md"
        disableOutsideClick={isSavingDraft}
        hideCloseButton
        className="max-w-[480px] p-6"
      >
        <DialogHeader className="flex-row items-start gap-3 space-y-0 text-left">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-amber-100">
            <AlertTriangle className="h-5 w-5 text-amber-600" aria-hidden="true" />
          </div>
          <div className="flex flex-col gap-1">
            <DialogTitle className="text-xl">Leave this consultation?</DialogTitle>
            <DialogDescription className="text-[15px] leading-relaxed">
              The consultation
              {patientName ? (
                <>
                  {' '}
                  with <span className="font-medium text-foreground">{patientName}</span>
                </>
              ) : (
                ''
              )}{' '}
              is still in progress. Unsaved notes and the live{' '}
              {isRecording ? 'recording' : 'transcription'} will be lost if you exit without saving.
            </DialogDescription>
          </div>
        </DialogHeader>

        <div className="flex flex-col gap-2.5 rounded-lg bg-muted/60 px-4 py-3.5 text-sm">
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Recording</span>
            <span className="font-semibold text-foreground">
              {formatDuration(recordingSeconds)} · {isRecording ? 'recording' : 'paused'}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">AI summary</span>
            <span className="font-semibold text-foreground">
              {symptomsCount} {symptomsCount === 1 ? 'symptom' : 'symptoms'}, {diagnosesCount}{' '}
              {diagnosesCount === 1 ? 'diagnosis' : 'diagnoses'}
            </span>
          </div>
        </div>

        <DialogFooter className="mt-1 flex-row items-center justify-between gap-3 border-t pt-4 sm:justify-between sm:space-x-0">
          <button
            type="button"
            onClick={onExitWithoutSaving}
            disabled={isSavingDraft}
            className="whitespace-nowrap text-sm font-medium text-destructive transition-colors hover:underline disabled:pointer-events-none disabled:opacity-50"
          >
            Exit without saving
          </button>
          <div className="flex items-center gap-2.5">
            <Button variant="outline" onClick={onSaveDraftAndExit} loading={isSavingDraft}>
              {isSavingDraft ? 'Saving draft…' : 'Save draft & exit'}
            </Button>
            <Button variant="primary" onClick={onStay} disabled={isSavingDraft}>
              Stay on page
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
